/**
 * SMTP Configuration Validation Utility
 * Checks if SMTP is properly configured before sending invoices/quotations
 */

/**
 * Check if SMTP is properly configured
 * @returns {Promise<{isConfigured: boolean, message?: string}>}
 */
export async function checkSmtpConfiguration() {
  try {
    // Import settings service dynamically
    const { default: settingsService } = await import('../services/settings.service');
    
    const smtpSettings = await settingsService.getSmtpSettings();
    
    if (!smtpSettings) {
      return {
        isConfigured: false,
        message: "SMTP settings not found. Please configure email settings in Settings."
      };
    }

    // Check if SMTP is enabled
    if (!smtpSettings.enabled) {
      return {
        isConfigured: false,
        message: "Email sending is disabled. Please enable email delivery in Settings."
      };
    }

    // Check required fields
    const requiredFields = ['smtp_host', 'smtp_user', 'smtp_password', 'from_email'];
    const missingFields = requiredFields.filter(field => !smtpSettings[field]);

    if (missingFields.length > 0) {
      return {
        isConfigured: false,
        message: "Email settings are incomplete. Please configure SMTP settings in Settings."
      };
    }

    return {
      isConfigured: true
    };
  } catch (error) {
    console.error('Error checking SMTP configuration:', error);
    return {
      isConfigured: false,
      message: "Failed to check email settings. Please try again."
    };
  }
}

/**
 * Show SMTP configuration warning dialog
 * @param {Function} showError - Error toast function
 * @param {Function} confirm - Confirmation dialog function
 * @param {Function} onNavigateToSettings - Function to navigate to settings
 * @returns {Promise<boolean>} - Whether user wants to proceed anyway
 */
export async function showSmtpWarning(showError, confirm, onNavigateToSettings) {
  const smtpCheck = await checkSmtpConfiguration();
  
  if (smtpCheck.isConfigured) {
    return true; // SMTP is configured, proceed
  }

  // First dialog: Ask if user wants to send anyway
  const sendAnyway = await confirm({
    title: "Email Not Configured",
    message: "Email settings are not configured. The invoice/quotation will be marked as sent but no email will be delivered to the client.\n\nDo you want to send anyway?",
    confirmText: "Send Anyway",
    cancelText: "Configure Email First",
    variant: "warning"
  });

  if (sendAnyway) {
    // User chose to send anyway
    return true;
  }

  // User chose to configure email first
  if (onNavigateToSettings) {
    onNavigateToSettings();
  }

  return false;
}